import "./Business.scss";
import { useEffect, useState, useMemo, useCallback } from "react";
import CustomCard from "../../components/CustomCard/CustomCard";
import { useSelector, useDispatch } from "react-redux";
import useSearchableTable from "../../hooks/useSearchableTable";
import useUserPrivileges from "../../hooks/useUserPrivileges";
import useDocDownloadError from "../../hooks/useDocDownloadError";
import TableHeader from "../../components/CustomTable/TableHeader";
import { getStatusList } from "../../redux/slices/EnqMngtSlice";
import { getBusinessView } from "../../redux/slices/BusSlice";
import BusTable from "./Components/BusTable";
import { downloadPageHandle, generateStatusTableTitle } from "../../helpers/helpers";
import { BUS_STATUS_DISPLAY_NAMES, STATUS } from "../../constants/constants";
import { BUS_STATUS_PRIVILEGE_MAP } from "../../constants/PermissionCodes";
import { count } from "../../redux/slices/dealsSlice";
import useModuleContext from "../../hooks/useModuleContext";

const IS_DEAL_STATUS_IDS = Object.values(STATUS).map((s) => s.ID);

const buildStatusTableTitle = ({ defaultTitle = "" } = {}) => defaultTitle || "";

const Business = () => {
  const dispatch = useDispatch();
  const { CountData } = useSelector((state) => state?.deals);
  const { statusList } = useSelector((state) => state?.enqMngt);
  const docLoading = useDocDownloadError();
  const { businessData } = useSelector((state) => state?.business);
  const { allowedStatusIds } = useUserPrivileges(BUS_STATUS_PRIVILEGE_MAP);
  const [columnControl, setColumnControl] = useState(null);
  const DEFAULT_BUS_STATUSES = useMemo(() => [STATUS.ACTIVE.ID], []);
  const defaultStatus = useMemo(() => {
    const preferred = DEFAULT_BUS_STATUSES.filter((id) =>
      allowedStatusIds.includes(id)
    );
    return preferred.length > 0 ? preferred : null;
  }, [allowedStatusIds, DEFAULT_BUS_STATUSES]);

  const {
    selectedStatus,
    handleStatusChange,
    searchText,
    handleSearchChange,
    pagination,
    applyDefaultStatus,
  } = useModuleContext({
    moduleKey: "business",
    isMultiStatus: true,
    allowedStatusIds,
    defaultStatus,
  });


  const { filteredData } = useSearchableTable(businessData, searchText, handleSearchChange);


  useEffect(() => {
    const fetchStatus = async () => {
      const res = await dispatch(getStatusList("bus"));
      if (res?.payload?.statusCode === 200) {
        const preferredStatuses = DEFAULT_BUS_STATUSES.filter((id) =>
          allowedStatusIds.includes(id),
        );
        const fallback =
          preferredStatuses.length > 0
            ? preferredStatuses
            : allowedStatusIds[0]
            ? [allowedStatusIds[0]]
            : [];
        applyDefaultStatus(fallback);
      }
    };
    fetchStatus();
  }, [dispatch, allowedStatusIds, applyDefaultStatus, DEFAULT_BUS_STATUSES]);

  useEffect(() => {
    if (selectedStatus) {
      dispatch(getBusinessView(selectedStatus));
    }
  }, [dispatch, selectedStatus]);

  useEffect(() => {
    dispatch(count("BUSINESS"));
  }, [dispatch]);

  const filteredStatusList = useMemo(
    () => statusList?.filter((status) => allowedStatusIds.includes(status.statusId)) ?? [],
    [statusList, allowedStatusIds],
  );

  const statusListWithCounts = useMemo(
    () => filteredStatusList.map((item) => {
      const isDeal = IS_DEAL_STATUS_IDS.includes(item.statusId);
      return {
        ...item,
        count: isDeal ? CountData?.find((c) => c.statusId === item.statusId)?.count || 0 : undefined,
      };
    }),
    [filteredStatusList, CountData],
  );

  const handleDownload = useCallback(
    (type) => downloadPageHandle({ dispatch, type, screenName: "business", statusId: selectedStatus }),
    [dispatch, selectedStatus],
  );

  const handleDownloadPdf = useCallback(() => handleDownload("pdf"), [handleDownload]);
  const handleDownloadExcel = useCallback(() => handleDownload("excel"), [handleDownload]);

  const tableTitle = useMemo(
    () =>
      (generateStatusTableTitle || buildStatusTableTitle)({
        defaultTitle: "Business",
      }),
    [],
  );

  return (
    <CustomCard>
      <TableHeader
        title={tableTitle}
        searchText={searchText}
        setSearchText={handleSearchChange}
        isDropdown={true}
        dropdownData={statusListWithCounts}
        selectedStatus={selectedStatus}
        handleStatusChange={handleStatusChange}
        statusDisplayNames={BUS_STATUS_DISPLAY_NAMES}
        isDownloadRequired={true}
        onDownloadPdf={handleDownloadPdf}
        onDownloadExcel={handleDownloadExcel}
        downloadLoading={docLoading}
        columnControl={columnControl}
      />
      <BusTable
        tableData={filteredData}
        selectedStatus={selectedStatus}
        onColumnControlChange={setColumnControl}
        pagination={pagination}
      />
    </CustomCard>
  );
};

export default Business;